import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { DashboardLayout } from "./Dashboard";
import { cn } from "@/lib/utils";
import {
  getUserUsageDetailed,
  ApiError,
  type DetailedUsageResponse,
} from "@/lib/api";
import {
  Loader2,
  AlertTriangle,
  Activity,
  Zap,
  BarChart,
  AlertCircle,
  BarChart2,
  X
} from "lucide-react";

type UsageData = DetailedUsageResponse["data"];

export function UsagePage() {
  const { getToken } = useAuth();
  const [timeRange, setTimeRange] = useState<7 | 30 | 90>(7);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<UsageData | null>(null);

  // Fetch usage data
  const fetchUsage = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getUserUsageDetailed(getToken, timeRange);
      setUsageData(data);
    } catch (err) {
      console.error("Failed to fetch usage:", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load usage data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getToken, timeRange]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Format dates
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Get day label for chart
  const getDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { weekday: "short" });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-sky-500 animate-spin mx-auto mb-3" />
            <p className="text-slate-500">Loading usage data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate chart max for scaling
  const maxRequests = usageData?.daily
    ? Math.max(...usageData.daily.map((d) => d.requests), 1)
    : 1;

  return (
    <DashboardLayout>
      {/* Header */}
      <header className="bg-transparent px-8 py-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">
              Usage Analytics
            </h1>
            <p className="text-slate-500">
              Monitor your API usage and performance
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
            {([7, 30, 90] as const).map((days) => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                  timeRange === days
                    ? "bg-slate-100 text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                )}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="px-8 pb-12">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <div className="p-1 bg-red-100 rounded-full text-red-600">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {usageData && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-sky-50 rounded-lg text-sky-600">
                    <Activity className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Total Requests</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {usageData.summary.total_requests.toLocaleString()}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Last {timeRange} days
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                    <Zap className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Cache Hit Rate</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {usageData.summary.cache_hit_rate.toFixed(1)}%
                </p>
                <p
                  className={cn(
                    "text-xs mt-1 font-medium",
                    usageData.summary.cache_hit_rate >= 70
                      ? "text-emerald-600"
                      : "text-amber-600",
                  )}
                >
                  {usageData.summary.cache_hit_rate >= 70
                    ? "Good Performance"
                    : "Could be improved"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                    <BarChart className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Avg Latency</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {usageData.summary.avg_latency_ms}ms
                </p>
                <p
                  className={cn(
                    "text-xs mt-1 font-medium",
                    usageData.summary.avg_latency_ms <= 100
                      ? "text-emerald-600"
                      : "text-amber-600",
                  )}
                >
                  {usageData.summary.avg_latency_ms <= 100
                    ? "Optimal"
                    : "Acceptable"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-red-50 rounded-lg text-red-600">
                    <AlertCircle className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">Error Rate</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {usageData.summary.error_rate.toFixed(2)}%
                </p>
                <p
                  className={cn(
                    "text-xs mt-1 font-medium",
                    usageData.summary.error_rate <= 1
                      ? "text-emerald-600"
                      : "text-red-600",
                  )}
                >
                  {usageData.summary.error_rate <= 1
                    ? "Excellent"
                    : "Needs attention"}
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Request Volume Chart */}
              <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="px-6 py-5 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">Request Volume</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatDate(usageData.period.start)} -{" "}
                    {formatDate(usageData.period.end)}
                  </p>
                </div>
                <div className="p-6">
                  {usageData.daily.length > 0 ? (
                    <>
                      <div className="h-48 flex items-end justify-around gap-2 px-2">
                        {usageData.daily.slice(-7).map((day, i) => (
                          <div
                            key={i}
                            className="flex-1 flex flex-col items-center gap-2 group"
                          >
                            <div
                              className="w-full bg-sky-500/90 rounded-t-sm transition-all relative group-hover:bg-sky-600"
                              style={{
                                height: `${Math.max(4, (day.requests / maxRequests) * 100)}%`,
                              }}
                            >
                              <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap transition-opacity">
                                {day.requests.toLocaleString()} requests
                              </div>
                            </div>
                            <span className="text-xs font-medium text-slate-400">
                              {getDayLabel(day.date)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-400 bg-slate-50/50 rounded-lg">
                      No data for this period
                    </div>
                  )}
                </div>
              </div>

              {/* Cache Performance Chart */}
              <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
                <div className="px-6 py-5 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">
                    Cache Performance
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {usageData.summary.cached_requests.toLocaleString()} cached
                    of {usageData.summary.total_requests.toLocaleString()} total
                  </p>
                </div>
                <div className="p-6">
                  {usageData.daily.length > 0 ? (
                    <>
                      <div className="h-48 flex items-end justify-around gap-2 px-2">
                        {usageData.daily.slice(-7).map((day, i) => {
                          const cacheRate =
                            day.requests > 0
                              ? (day.cached / day.requests) * 100
                              : 0;
                          return (
                            <div
                              key={i}
                              className="flex-1 flex flex-col items-center gap-2 group"
                            >
                              <div
                                className="w-full bg-emerald-500/90 rounded-t-sm transition-all relative group-hover:bg-emerald-600"
                                style={{
                                  height: `${Math.max(4, cacheRate)}%`,
                                }}
                              >
                                <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap transition-opacity">
                                  {cacheRate.toFixed(1)}% hit rate
                                </div>
                              </div>
                              <span className="text-xs font-medium text-slate-400">
                                {getDayLabel(day.date)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-center gap-6 mt-6">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" />
                          <span className="text-xs text-slate-600">
                            Cache Hit %
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-400 bg-slate-50/50 rounded-lg">
                      No data for this period
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Endpoint Breakdown */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] mb-8">
              <div className="px-6 py-5 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800">
                  Requests by Endpoint
                </h3>
              </div>
              <div className="p-6">
                {usageData.endpoints.length > 0 ? (
                  <div className="space-y-5">
                    {usageData.endpoints.map((endpoint) => (
                      <div key={endpoint.endpoint}>
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-3">
                            <span className="p-1 bg-slate-100 rounded text-xs font-mono text-slate-600">GET</span>
                            <code className="text-sm font-medium text-slate-700">
                              {endpoint.endpoint}
                            </code>
                          </div>
                          <span className="text-sm text-slate-500 font-medium">
                            {endpoint.requests.toLocaleString()} <span className="text-slate-400 font-normal">({endpoint.percentage}%)</span>
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-sky-500 rounded-full transition-all"
                            style={{ width: `${endpoint.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 flex flex-col items-center">
                    <div className="p-3 bg-slate-50 rounded-full mb-3">
                      <BarChart2 className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-900 font-medium">No usage data available</p>
                    <p className="text-slate-500 text-sm">
                      Start making API calls to see your endpoint statistics.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Usage Quota */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                    <Activity className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Monthly Quota</h3>
                    <p className="text-sm text-slate-500">
                      Resets on the 1st of each month
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">
                    {usageData.quota.used.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-500">
                    of{" "}
                    {usageData.quota.limit === Infinity
                      ? "Unlimited"
                      : usageData.quota.limit.toLocaleString()}{" "}
                    requests
                  </p>
                </div>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-1000",
                    usageData.quota.percentage > 90
                      ? "bg-red-500"
                      : usageData.quota.percentage > 75
                        ? "bg-amber-500"
                        : "bg-sky-500",
                  )}
                  style={{
                    width: `${Math.min(100, usageData.quota.percentage)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-slate-500">
                {usageData.quota.percentage.toFixed(1)}% used •{" "}
                {usageData.quota.remaining.toLocaleString()} requests remaining
              </p>
            </div>
          </>
        )}

        {/* Empty state */}
        {!usageData && !error && !isLoading && (
          <div className="text-center py-16 flex flex-col items-center">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
              <BarChart2 className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No usage data yet
            </h3>
            <p className="text-slate-500 max-w-sm mx-auto">
              Start making API calls to see your usage analytics here.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
